import { pagedList } from './pagedList';
import { del, get, searchAllPages, post } from '@/lib/apiClient';
import { endpoints } from './endpoints';
import { schoolScope } from './schoolScope';
import type {
  AcademicClass,
  AcademicClassInput,
  AcademicSection,
  AcademicSectionInput,
  AcademicSubject,
  AcademicSubjectInput,
  AcademicYear,
  AcademicYearAccess,
  AcademicYearInput,
  AcademicYearRolloverInput,
  AcademicYearRolloverPreview,
  AcademicYearRolloverResult,
  AcademicYearStatus,
  ArrearsCarryResult,
  ClassSection,
  ClassSectionInput,
  ClassSectionSubject,
  ClassSectionSubjectInput,
  House,
  HouseInput,
  RolloverFinishInput,
} from '@/models';

const f = endpoints.foundation;

// --- Academic years (Foundation API) ---------------------------------------

/** `AcademicYearOutput` as returned by the API. */
interface AcademicYearDto {
  academicYearId: number;
  yearName: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  status: AcademicYearStatus;
}

const toYear = (dto: AcademicYearDto): AcademicYear => ({
  yearId: dto.academicYearId,
  yearName: dto.yearName,
  yearStartDate: dto.startDate,
  yearEndDate: dto.endDate,
  status: dto.status,
  isCurrent: dto.isCurrent,
});

export const listYears = async (schoolId: number) =>
  (await searchAllPages<AcademicYearDto>(f.academicYears(), {}, await schoolScope(schoolId))).map(toYear);

export const saveYear = async (schoolId: number, yearId: number | undefined, input: AcademicYearInput) =>
  toYear(
    yearId
      ? await post<AcademicYearDto>(f.academicYear(yearId), input, await schoolScope(schoolId))
      : await post<AcademicYearDto>(f.academicYears(), input, await schoolScope(schoolId)),
  );

export const setCurrentYear = async (schoolId: number, yearId: number) =>
  toYear(await post<AcademicYearDto>(f.setCurrentAcademicYear(yearId), undefined, await schoolScope(schoolId)));

export const deleteYear = async (schoolId: number, yearId: number) =>
  del<void>(f.academicYear(yearId), await schoolScope(schoolId));

/** Whether the caller may edit closed or ended years and run the rollover, in the school's branch. */
export const getAcademicYearAccess = async (schoolId: number) =>
  get<AcademicYearAccess>(f.academicYearAccess(), await schoolScope(schoolId));

// --- Year rollover ("Start new year") -----------------------------------------
// Every route names the target year; the source year travels in the query or body.

/** `steps` (repeated `steps=` query keys) previews just those steps, so dependency warnings fit the choice. */
export const getRolloverPreview = async (
  schoolId: number,
  toYearId: number,
  fromYearId: number,
  steps?: readonly string[] | null,
) =>
  post<AcademicYearRolloverPreview>(
    f.rolloverPreview(toYearId),
    { fromAcademicYearId: fromYearId, ...(steps ? { steps } : {}) },
    await schoolScope(schoolId),
  );

/** A rollover copies a whole year's setup; give it longer than the default 30 s request timeout. */
const ROLLOVER_TIMEOUT_MS = 180_000;

export const runRollover = async (schoolId: number, toYearId: number, input: AcademicYearRolloverInput) =>
  post<AcademicYearRolloverResult>(f.rollover(toYearId), input, {
    ...(await schoolScope(schoolId)),
    timeout: ROLLOVER_TIMEOUT_MS,
  });

export const carryArrears = async (schoolId: number, toYearId: number, fromYearId: number) =>
  post<ArrearsCarryResult>(
    f.rolloverArrears(toYearId),
    { fromAcademicYearId: fromYearId },
    { ...(await schoolScope(schoolId)), timeout: ROLLOVER_TIMEOUT_MS },
  );

/** Makes the target year current (and closes the source year when asked); returns the target year. */
export const finishRollover = async (schoolId: number, toYearId: number, input: RolloverFinishInput) =>
  toYear(await post<AcademicYearDto>(f.rolloverFinish(toYearId), input, await schoolScope(schoolId)));

// --- Classes (Foundation API) ----------------------------------------------
// ClassOutput matches AcademicClass field for field.

export const listClasses = async (schoolId: number) => searchAllPages<AcademicClass>(f.classes(), {}, await schoolScope(schoolId));

export const saveClass = async (schoolId: number, classId: number | undefined, input: AcademicClassInput) =>
  classId
    ? post<AcademicClass>(f.class(classId), input, await schoolScope(schoolId))
    : post<AcademicClass>(f.classes(), input, await schoolScope(schoolId));

export const checkClassCode = async (schoolId: number, classCode: string, classId?: number) =>
  get<{ available: boolean }>(f.classes() + '/code-availability',
    { ...await schoolScope(schoolId), params: { classCode, exceptClassId: classId } });

export const deleteClass = async (schoolId: number, classId: number) => del<void>(f.class(classId), await schoolScope(schoolId));

// --- Sections (Foundation API) ---------------------------------------------
// SectionOutput matches AcademicSection field for field.

export const listSections = async (schoolId: number) => searchAllPages<AcademicSection>(f.sections(), {}, await schoolScope(schoolId));

export const saveSection = async (schoolId: number, sectionId: number | undefined, input: AcademicSectionInput) =>
  sectionId
    ? post<AcademicSection>(f.section(sectionId), input, await schoolScope(schoolId))
    : post<AcademicSection>(f.sections(), input, await schoolScope(schoolId));

export const deleteSection = async (schoolId: number, sectionId: number) =>
  del<void>(f.section(sectionId), await schoolScope(schoolId));

// --- Subjects (Foundation API) ---------------------------------------------
// SubjectOutput matches AcademicSubject field for field.

export const listSubjects = async (schoolId: number) => searchAllPages<AcademicSubject>(f.subjects(), {}, await schoolScope(schoolId));

export const saveSubject = async (schoolId: number, subjectId: number | undefined, input: AcademicSubjectInput) =>
  subjectId
    ? post<AcademicSubject>(f.subject(subjectId), input, await schoolScope(schoolId))
    : post<AcademicSubject>(f.subjects(), input, await schoolScope(schoolId));

export const deleteSubject = async (schoolId: number, subjectId: number) =>
  del<void>(f.subject(subjectId), await schoolScope(schoolId));

// --- Class sections (Foundation API) ---------------------------------------
// ClassSectionOutput matches ClassSection field for field.

/** Every class-section of the school. */
export const listClassSections = async (schoolId: number) =>
  searchAllPages<ClassSection>(f.schoolClassSections(), {}, await schoolScope(schoolId));

export const saveClassSection = (classId: number, classSectionId: number | undefined, input: ClassSectionInput) =>
  classSectionId
    ? post<ClassSection>(f.classSection(classId, classSectionId), input)
    : post<ClassSection>(f.classSections(classId), input);

export const deleteClassSection = (classId: number, classSectionId: number) =>
  del<void>(f.classSection(classId, classSectionId));

// --- Houses (Foundation API) -----------------------------------------------
// HouseOutput matches House field for field. The API pages and filters; the
// grid loads every page and filters locally, like the other tabs.

export const listHouses = async (schoolId: number) => searchAllPages<House>(f.houses(), {}, await schoolScope(schoolId));

export const saveHouse = async (schoolId: number, houseId: number | undefined, input: HouseInput) =>
  houseId
    ? post<House>(f.house(houseId), input, await schoolScope(schoolId))
    : post<House>(f.houses(), input, await schoolScope(schoolId));

export const deleteHouse = async (schoolId: number, houseId: number) =>
  del<void>(f.house(houseId), await schoolScope(schoolId));

// --- Class section subjects (Foundation API) ---------------------------------
// ClassSectionSubjectOutput matches ClassSectionSubject field for field.

/** Every subject assignment of the school. */
export const listClassSectionSubjects = async (schoolId: number) =>
  searchAllPages<ClassSectionSubject>(f.schoolClassSectionSubjects(), {}, await schoolScope(schoolId));

export const saveClassSectionSubject = (
  classSectionId: number,
  classSectionSubjectId: number | undefined,
  input: ClassSectionSubjectInput,
) =>
  classSectionSubjectId
    ? post<ClassSectionSubject>(f.classSectionSubject(classSectionId, classSectionSubjectId), input)
    : post<ClassSectionSubject>(f.classSectionSubjects(classSectionId), input);

export const deleteClassSectionSubject = (classSectionId: number, classSectionSubjectId: number) =>
  del<void>(f.classSectionSubject(classSectionId, classSectionSubjectId));

// Server-driven table lists.
export const listYearsPage = pagedList<AcademicYearDto, ReturnType<typeof toYear>>(f.academicYears(), toYear);
export const listClassesPage = pagedList<AcademicClass>(f.classes());
export const listSectionsPage = pagedList<AcademicSection>(f.sections());
export const listHousesPage = pagedList<House>(f.houses());

export const searchClasses = async (schoolId: number, params: Record<string, unknown>, signal?: AbortSignal) => searchAllPages<AcademicClass>(f.classes(), params, { ...await schoolScope(schoolId), signal });

export const searchClassSectionSubjects = async (schoolId: number, params: Record<string, unknown>, signal?: AbortSignal) => searchAllPages<ClassSectionSubject>(f.schoolClassSectionSubjects(), params, { ...await schoolScope(schoolId), signal });

export const searchClassSections = async (schoolId: number, params: Record<string, unknown>, signal?: AbortSignal) => searchAllPages<ClassSection>(f.schoolClassSections(), params, { ...await schoolScope(schoolId), signal });
